import React, { ReactNode, useEffect } from "react";
import "../styles/Modal.scss";
import { motion, useDragControls, useMotionValue } from "framer-motion";
import { isMobile } from "react-device-detect";
import {
	desktopModalFade,
	fadeScale,
	fadeTransition,
	mobileModalFade,
} from "../styles/MotionFramerAnimations";
import { BackIcon, CloseIcon } from "../svgIcons";
import { useModalHeight } from "../hooks/useModalHeight";
type ModalProps = {
	onClose: () => void;
	children?: ReactNode;
	title?: string;
	backBtnLabel?: string;
	onBack?: () => void;
	isAlert?: boolean; // message/alert: centered content-sized popup on mobile too, no drag bar
};

export const Modal: React.FC<ModalProps> = ({
	onClose,
	children,
	title,
	backBtnLabel = null,
	onBack = null,
	isAlert = false,
}) => {
	const { height, contentRef } = useModalHeight();
	const controls = useDragControls();
	const y = useMotionValue(0);

	const handleDragEnd = () => {
		if (y.get() > 100) {
			onClose();
		}
	};

	useEffect(() => {
		document.body.classList.add("simplex-club-modal-open");
		return () => {
			document.body.classList.remove("simplex-club-modal-open");
		};
	}, []);

	useEffect(() => {
		const handleEsc = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("keydown", handleEsc);

		return () => {
			document.removeEventListener("keydown", handleEsc);
		};
	}, [onClose]);

	return (
		<>
			<motion.div
				{...fadeTransition}
				className='club-modal-overlay'
				aria-hidden='true'
				style={{ willChange: "auto" }}
			></motion.div>
			<motion.div
				drag={isAlert ? false : "y"}
				style={{ y, willChange: "auto" }}
				dragListener={false}
				dragControls={controls}
				dragConstraints={{ top: 0, bottom: 0 }}
				dragElastic={{ top: 0, bottom: 1 }}
				onDragEnd={handleDragEnd}
				className={`club-modal${isAlert ? " alert" : ""}`}
				role='dialog'
				onClick={e => e.stopPropagation()}
				{...(isMobile
					? isAlert
						? fadeScale
						: mobileModalFade
					: desktopModalFade)}
			>
				<div className='club-modal-header-actions'>
					{backBtnLabel && onBack && (
						<button
							className='club-modal-button-back'
							onClick={onBack}
						>
							<BackIcon />
							<span>{backBtnLabel}</span>
						</button>
					)}
					<button
						className='club-modal-button-close'
						onClick={onClose}
					>
						{CloseIcon()}
					</button>
				</div>

				{isMobile && !isAlert && (
					<div
						className='drag-bar-wrapper'
						onPointerDown={e => controls.start(e)}
						role='button'
					>
						<span className='drag-bar-line' />
					</div>
				)}
				<div
					ref={contentRef}
					style={{ maxHeight: isAlert ? undefined : height }}
					className='club-modal-content'
				>
					{title && <h2 className='modal-title'>{title}</h2>}
					{children}
				</div>
			</motion.div>
		</>
	);
};
